import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReadOnlyRichText } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  DescriptionCard,
  DescriptionCloseButton,
  DescriptionContent,
  DescriptionHeader,
  DescriptionTitle,
} from './CategoryDescriptionPopup.styles';

interface Props {
  title: string;
  descriptionHtml: string;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

export const CategoryDescriptionPopup = ({
  title,
  descriptionHtml,
  onClose,
  triggerRef,
}: Props) => {
  const cardRef = useRef<HTMLElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!triggerRef.current) return;

    const trigger = triggerRef.current;
    let cardElement = trigger.closest('[role="button"]') as HTMLElement;

    if (!cardElement) {
      cardElement = trigger.closest(
        '[data-testid*="categories-section-card"]',
      ) as HTMLElement;
    }

    if (!cardElement) return;

    let rafId: number | null = null;

    const updatePosition = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const bounds = cardElement.getBoundingClientRect();
        setRect({
          top: bounds.top,
          left: bounds.left,
        });
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [triggerRef]);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideCard = cardRef.current?.contains(target);
      const isInsideTrigger = triggerRef.current?.contains(target);

      if (!isInsideCard && !isInsideTrigger) {
        onClose();
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [onClose, triggerRef]);

  if (!rect) return null;

  return createPortal(
    <DescriptionCard
      ref={cardRef}
      data-testid="category-description-popup"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      style={{ top: `${rect.top}px`, left: `${rect.left}px` }}
    >
      <DescriptionHeader>
        <DescriptionTitle>{title}</DescriptionTitle>
        <DescriptionCloseButton
          type="button"
          aria-label="Close description"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onClose();
          }}
          data-testid="category-description-popup-close"
        >
          <StyledIcon className="uil uil-times" size="24px" isClickable />
        </DescriptionCloseButton>
      </DescriptionHeader>

      <DescriptionContent>
        <ReadOnlyRichText
          value={descriptionHtml}
          data-testid="category-description-popup-content"
        />
      </DescriptionContent>
    </DescriptionCard>,
    document.body,
  );
};
